/**
 * Split `target` whole units across weighted keys so the parts sum to it
 * exactly. Largest remainder; ties go to the heavier weight, then the key, so
 * the same input always gives the same split.
 *
 * Lives in its own module so both the budget maths and the page's percentage
 * shares can use it without importing each other.
 */
export function apportion(items, target) {
  const sum = items.reduce((acc, i) => acc + Math.max(0, i.weight), 0)
  const out = new Map()
  if (sum <= 0 || target <= 0) {
    items.forEach(i => out.set(i.key, 0))
    return out
  }
  const parts = items.map(i => {
    const exact = (Math.max(0, i.weight) / sum) * target
    return { ...i, floor: Math.floor(exact), rem: exact - Math.floor(exact) }
  })
  let left = target - parts.reduce((acc, p) => acc + p.floor, 0)
  const order = [...parts].sort((a, b) => (b.rem - a.rem) || (b.weight - a.weight) || String(a.key).localeCompare(String(b.key)))
  for (const p of order) {
    if (left <= 0) break
    p.floor += 1
    left -= 1
  }
  parts.forEach(p => out.set(p.key, p.floor))
  return out
}

/**
 * Whole-number percentages of a set of counts that add up to exactly 100
 * (largest remainder), for figures presented as parts of a whole. Independent
 * rounding shows 33 + 33 + 33 or 34 + 34 + 33 + 0 depending on luck; this never
 * does. All-zero input gives all zeros.
 *
 * @param values array of non-negative numbers
 * @returns array of integers, same order
 */
export function percentParts(values) {
  const list = (values || []).map(v => Math.max(0, Number(v) || 0))
  const shares = apportion(list.map((weight, key) => ({ key, weight })), 100)
  return list.map((_, i) => shares.get(i) || 0)
}
